import { useEffect, useMemo, useState } from 'react'
import { BarChart3, Check, Clock3, Flame, Share2, TrendingUp, UsersRound } from 'lucide-react'
import { api } from '../lib/api.js'
import { accountApi } from '../lib/accountApi.js'
import { calculateProgress, readLocalHistory } from '../lib/progressStore.js'
import { track } from '../lib/telemetry.js'
import { NextDailyCountdown } from './PlayerProgress.jsx'

function secondsOf(value) {
  const milliseconds = Number(value?.elapsedMs ?? value?.elapsed_ms)
  if (Number.isFinite(milliseconds)) return milliseconds / 1000
  const seconds = Number(value?.elapsedSeconds ?? value?.elapsed_seconds ?? value?.time)
  return Number.isFinite(seconds) ? seconds : null
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return '—'
  const total = Math.max(0, Math.floor(seconds))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

function median(values) {
  if (!values.length) return null
  const sorted = [...values].sort((left, right) => left - right)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

function comparisonLabel(ownSeconds, medianSeconds) {
  if (!Number.isFinite(ownSeconds) || !Number.isFinite(medianSeconds)) return null
  const delta = Math.round(Math.abs(ownSeconds - medianSeconds))
  if (delta < 2) return 'Dans la médiane du jour'
  return ownSeconds < medianSeconds
    ? `${formatTime(delta)} plus rapide que la médiane`
    : `${formatTime(delta)} après la médiane`
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  const copied = document.execCommand('copy')
  textarea.remove()
  if (!copied) throw new Error('Copie impossible')
}

export function DailyRecap({ day, result, nickname, onLeaderboard, onProgress, onReview }) {
  const [summary, setSummary] = useState(null)
  const [shareStatus, setShareStatus] = useState('')
  const ownSeconds = secondsOf(result)

  useEffect(() => {
    let active = true
    Promise.all([
      api.leaderboard(100, day),
      accountApi.stats().catch(() => null),
    ]).then(([payload, statsPayload]) => {
      if (!active) return
      const entries = payload.entries || payload.leaderboard || []
      const ownEntry = entries.find((entry) => Number(entry.rank) === Number(result?.rank))
        || entries.find((entry) => (entry.nickname || entry.pseudo) === nickname)
      const rank = Number(result?.rank ?? ownEntry?.rank) || null
      const participants = Number(
        result?.participants
        ?? payload.summary?.participants
        ?? payload.total
        ?? payload.count
        ?? entries.length,
      ) || null
      const serverMedianMs = Number(result?.medianElapsedMs ?? payload.summary?.medianElapsedMs)
      const medianSeconds = Number.isFinite(serverMedianMs)
        ? serverMedianMs / 1_000
        : median(entries.map(secondsOf).filter(Number.isFinite))
      const serverPercentile = Number(result?.percentile ?? payload.summary?.percentile)
      const next = {
        rank,
        participants,
        medianSeconds,
        percentile: Number.isFinite(serverPercentile)
          ? serverPercentile
          : rank && participants && rank <= participants
          ? Math.max(0, Math.round(((participants - rank + 1) / participants) * 100))
          : null,
        currentStreak: Number(statsPayload?.stats?.currentStreak ?? statsPayload?.currentStreak) || null,
      }
      setSummary(next)
      onProgress?.(next)
    }).catch(() => {})
    return () => { active = false }
  }, [day, nickname, result?.rank, onProgress])

  const streak = useMemo(
    () => summary?.currentStreak ?? calculateProgress(readLocalHistory()).currentStreak,
    [day, summary],
  )
  const comparison = comparisonLabel(ownSeconds, summary?.medianSeconds)

  const share = async () => {
    const dayLabel = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' })
      .format(new Date(`${day}T12:00:00.000Z`))
    const rankLine = summary?.rank
      ? `🏅 #${summary.rank}${summary.participants ? ` sur ${summary.participants}` : ''}`
      : null
    const text = [
      `Sukoji du ${dayLabel} ✅`,
      `⏱ ${formatTime(ownSeconds)}`,
      rankLine,
      streak ? `🔥 Série de ${streak} jour${streak > 1 ? 's' : ''}` : null,
      `${window.location.origin}/jouer/quotidien`,
    ].filter(Boolean).join('\n')
    track('share_click', { source: 'daily_recap', method: navigator.share ? 'native' : 'clipboard' })
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Mon Sukoji du jour', text })
        setShareStatus('Partagé !')
      } else {
        await copyText(text)
        setShareStatus('Résultat copié !')
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        try { await copyText(text); setShareStatus('Résultat copié !') } catch { setShareStatus('Partage indisponible') }
      }
    }
  }

  return (
    <>
      <p className="victory-copy">{nickname}, votre résultat rejoint le classement du jour.</p>
      <div className="daily-recap-primary">
        <div><span>Votre temps</span><strong>{formatTime(ownSeconds)}</strong></div>
        <div><span>Votre rang</span><strong>{summary?.rank || result?.rank ? `#${summary?.rank || result.rank}` : '—'}</strong>{summary?.participants && <small>sur {summary.participants}</small>}</div>
      </div>
      <div className="daily-recap-insights">
        {Number.isFinite(summary?.percentile) && (
          <span><TrendingUp size={17} /><strong>Devant {summary.percentile} %</strong> des joueurs</span>
        )}
        {comparison && <span><Clock3 size={17} /><strong>{comparison}</strong></span>}
        {streak > 0 && <span><Flame size={17} /><strong>{streak} jour{streak > 1 ? 's' : ''}</strong> de série</span>}
        {summary?.participants && <span><UsersRound size={17} /><strong>{summary.participants}</strong> participation{summary.participants > 1 ? 's' : ''}</span>}
      </div>
      <NextDailyCountdown compact />
      <div className="victory-actions">
        <button className="button button--primary button--full" type="button" onClick={share}><Share2 size={18} /> Partager sans spoiler</button>
        <button className="button button--ghost button--full" type="button" onClick={onLeaderboard}><BarChart3 size={18} /> Voir le classement</button>
        <button className="text-link recap-review" type="button" onClick={onReview}>Revoir la grille</button>
      </div>
      {shareStatus && <p className="share-status" role="status"><Check size={15} /> {shareStatus}</p>}
    </>
  )
}
